/**
 * EXTERIOR TRIM + FINISH RETURNS (2026-10-02, Matt: "I want exterior finishes to turn into the
 * windows and doors openings to the frames … unless there are trims turned on … a toggle to turn
 * on or off window and door trims … different styles … when windows/doors are mulled together or
 * too close to fit trims between them, their trims combine to surround the whole opening").
 *
 * Styles (decided with Matt, 2026-10-02; doors get each style minus its bottom pieces):
 *   none          — no trim: the wall's exterior finish RETURNS into the opening to the frame
 *   traditional   — stiles + head + sill (nosing, horns) + apron
 *   pictureFrame  — one casing width all the way around, no sill
 *   stoneSill     — the finish returns at the jambs + head, over a thick stone/wood sill
 *   modern        — 1" boards lining the opening from the frame out, standing proud ("tube")
 *
 * Settings: a TYPE default (`exteriorTrimStyle` + sizes + materials — firm standards) that any
 * one opening overrides with `exteriorTrim` ('' = the type's, 'none' = off, or a style). The
 * geometry is the HOST WALL's (bim-geometry categories/wall/openingTrim.ts) — it alone knows the
 * finish thickness, the frame's depth in the wall and its neighbours for mulled grouping.
 *
 * Where the frame sits (FRAME FACES, shared by plan and 3D so they can never disagree): an
 * EXTERIOR-mounted frame's outer face is the face of the sheathing — the wall's exterior face
 * minus the exterior FINISH layers — so the finish has a depth to return through.
 */
import type { AssemblyRecord, ParamValue } from './types'
import { wallFaceOffsets } from './locationLine'
import type { FrameMount } from './openingFrame'
import { masonryUnitOf } from './masonry'

export const EXTERIOR_TRIM_STYLES = ['none', 'traditional', 'pictureFrame', 'stoneSill', 'brick', 'modern'] as const
export type ExteriorTrimStyle = (typeof EXTERIOR_TRIM_STYLES)[number]

export const EXTERIOR_TRIM_LABELS: Record<ExteriorTrimStyle, string> = {
  none: 'None — finish returns to the frame',
  traditional: 'Traditional — stiles, head, sill & apron',
  pictureFrame: 'Picture frame — casing all around',
  stoneSill: 'Thick sill — finish returns, stone/wood sill',
  brick: 'Brick — rowlock sill & brick lintel (masonry walls)',
  modern: 'Modern — 1" boards lining the opening ("tube")',
}

const IN = 1 / 12
/** the firm-standard sizes (feet) — Matt approved these defaults 2026-10-02 */
export const EXTERIOR_TRIM_DEFAULTS = {
  trimWidthFt: 3.5 * IN,       // stiles / picture-frame casing
  trimHeadFt: 5.5 * IN,        // traditional head
  trimThickFt: 1 * IN,
  sillThickFt: 2 * IN,         // traditional sill nosing
  sillProjectFt: 1.5 * IN,     // past the casing face
  sillHornFt: 1 * IN,          // past the casing, each end
  apronFt: 3.5 * IN,
  stoneSillThickFt: 3 * IN,
  stoneSillProjectFt: 2 * IN,  // past the wall finish
  stoneSillHornFt: 2 * IN,     // past the opening, each end
  tubeThickFt: 1 * IN,
  tubeProjectFt: 2 * IN,       // proud of the wall finish
  jambExtThickFt: 0.75 * IN,   // the trim-material lining from the casing back to the frame
  // BRICK (2026-10-04, Matt: "a rowlock style sill that tilts out slightly and a header course
  // lintel … for brick and stone walls"): the sill is a course of bricks ON EDGE with their
  // ends showing (3-5/8" tall at the front, 2-1/4" + a 3/8" joint per brick along the sill),
  // washed down to the outside; the lintel is a course over the head — a SOLDIER course by
  // default (Matt, same day: "bricks … turned on their side with their tall sides going up
  // and down")
  brickSillHeightFt: 3.625 * IN,
  brickSillProjectFt: 1.5 * IN,  // past the wall finish
  brickSillHornFt: 2 * IN,       // past the opening, each end
  brickSillSlopeDeg: 15,         // the wash: tilted down to the outside
  lintelEndsFt: 4 * IN,          // past the opening, each end
  lintelProjectFt: 0.25 * IN,    // proud of the wall finish (0 = flush)
}

/** BRICK lintel courses: the brick face shown, height x width per unit (feet, 3/8" joint in) */
export const LINTEL_COURSES = {
  soldier: { label: 'Soldier course (bricks standing on end)', heightFt: 7.625 * IN, unitFt: (2.25 + 0.375) * IN },
  rowlock: { label: 'Rowlock course (brick ends, on edge)', heightFt: 3.625 * IN, unitFt: (2.25 + 0.375) * IN },
  header: { label: 'Header course (brick ends, flat)', heightFt: 2.25 * IN, unitFt: (3.625 + 0.375) * IN },
} as const
export type LintelCourse = keyof typeof LINTEL_COURSES
/**
 * A lintel course's height. Laid in a known brick (`brickUnit`, a MASONRY_UNITS key) it is that
 * brick turned (2026-10-06, Matt: "the same brick just turned in a different way") — a soldier
 * its length, a rowlock its bed depth, a header its thickness (a modular brick gives exactly the
 * curated heights); else the curated height.
 */
export function lintelCourseHeightFt(course: LintelCourse, brickUnit: string | null | undefined): number {
  if (!brickUnit) return LINTEL_COURSES[course].heightFt
  const u = masonryUnitOf(brickUnit), J = 0.375 * IN
  // a LONG THIN brick stood on end is far too tall over an opening (2026-10-06, Matt: "make
  // them half their current height in that configuration"): its soldier lintel is laid in HALF
  // bricks — 7-5/8", the same height as a modular soldier — still the long thin brick's width
  if (course === 'soldier' && brickUnit === 'longThin') return u.lengthFt / 2 - J
  return (course === 'soldier' ? u.lengthFt : course === 'rowlock' ? u.headerFt : u.courseFt) - J
}
/** the opening's own `lintel` pick, else the type's `lintelCourse`, else soldier */
export function resolveLintelCourse(p: Record<string, ParamValue | undefined>): LintelCourse {
  for (const v of [p.lintel, p.lintelCourse]) if (typeof v === 'string' && v in LINTEL_COURSES) return v as LintelCourse
  return 'soldier'
}
/** the rowlock sill's unit along the sill (one brick on edge + a joint) */
export const ROWLOCK_UNIT_FT = (2.25 + 0.375) * IN

const styleOf = (v: unknown): ExteriorTrimStyle | null =>
  typeof v === 'string' && (EXTERIOR_TRIM_STYLES as readonly string[]).includes(v) ? (v as ExteriorTrimStyle) : null

/** the resolved style for one opening: instance override, else the type's, else none */
export function resolveExteriorTrimStyle(p: Record<string, ParamValue | undefined>): ExteriorTrimStyle {
  return styleOf(p.exteriorTrim) ?? styleOf(p.exteriorTrimStyle) ?? 'none'
}

/** every size, from the merged type+instance params (missing/invalid → the firm default) */
export function resolveExteriorTrimSizes(p: Record<string, ParamValue | undefined>): typeof EXTERIOR_TRIM_DEFAULTS {
  const out = { ...EXTERIOR_TRIM_DEFAULTS }
  for (const k of Object.keys(out) as (keyof typeof out)[]) {
    const v = p[k]
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) out[k] = v
  }
  return out
}

/** total thickness of the EXTERIOR finish layers (layers[0…] while function === 'finish') */
export function exteriorFinishThicknessFt(assembly: AssemblyRecord | null | undefined): number {
  let t = 0
  for (const l of assembly?.layers ?? []) {
    if (l.function !== 'finish') break
    t += l.thicknessFt || 0
  }
  return t
}

/**
 * FRAME FACES — where the frame sits across the wall, as signed offsets from the wall
 * CENTERLINE along its left normal (the `wallFaceOffsets` convention). `lo`/`hi` bound the
 * frame; `outerFace` is the face it is flush to (exterior mount: the SHEATHING face — the wall's
 * exterior face moved in by the finish thickness), `otherFace` the wall face the jamb extension
 * runs to. Centered keeps the pre-round ±depth/2. A frame deeper than the wall never overshoots.
 */
export function openingFrameFaces(
  mount: FrameMount,
  flipped: ParamValue | undefined,
  assembly: AssemblyRecord | null | undefined,
  frameDepthFt: number,
  /** PROJECT STYLE phase 2: an exterior-mounted frame's recess behind the exterior FINISH face
   *  (a deep stucco return) — never less than flush to the sheathing; null/0 = flush */
  recessFt?: ParamValue | null,
): { lo: number; hi: number; outerFace: number; otherFace: number; inner: number } {
  if (mount === 'centered') {
    return { lo: -frameDepthFt / 2, hi: frameDepthFt / 2, outerFace: 0, otherFace: 0, inner: 0 }
  }
  const { exteriorOff, interiorOff } = wallFaceOffsets(flipped, assembly)
  const sE = Math.sign(exteriorOff - interiorOff) || 1   // exterior direction along the normal
  const tf = exteriorFinishThicknessFt(assembly)
  const recess = typeof recessFt === 'number' && recessFt > 0 ? recessFt : 0
  const outerFace = mount === 'exterior' ? exteriorOff - sE * Math.max(tf, recess) : interiorOff
  const otherFace = mount === 'exterior' ? interiorOff : exteriorOff
  const dir = Math.sign(otherFace - outerFace) || 1
  const rawInner = outerFace + dir * frameDepthFt
  const inner = dir > 0 ? Math.min(rawInner, otherFace) : Math.max(rawInner, otherFace)
  return { lo: Math.min(outerFace, inner), hi: Math.max(outerFace, inner), outerFace, otherFace, inner }
}

const len = (key: string, label: string, def: number, styles: ExteriorTrimStyle[]) => ({
  key, label, kind: 'length' as const, default: def, min: 0, max: 2,
  conditionalOn: { param: 'exteriorTrimStyle', equals: styles },
})

/** the TYPE params group — shared by the door and window schemas */
export const EXTERIOR_TRIM_TYPE_GROUP = {
  label: 'Exterior Trim',
  params: [
    {
      key: 'exteriorTrimStyle', label: 'Trim Style', kind: 'choice' as const, default: 'none',
      choices: EXTERIOR_TRIM_STYLES.map((v) => ({ value: v, label: EXTERIOR_TRIM_LABELS[v] })),
    },
    len('trimWidthFt', 'Casing Width', EXTERIOR_TRIM_DEFAULTS.trimWidthFt, ['traditional', 'pictureFrame']),
    len('trimHeadFt', 'Head Height', EXTERIOR_TRIM_DEFAULTS.trimHeadFt, ['traditional']),
    len('trimThickFt', 'Casing Thickness', EXTERIOR_TRIM_DEFAULTS.trimThickFt, ['traditional', 'pictureFrame']),
    len('sillThickFt', 'Sill Thickness', EXTERIOR_TRIM_DEFAULTS.sillThickFt, ['traditional']),
    len('sillProjectFt', 'Sill Projection', EXTERIOR_TRIM_DEFAULTS.sillProjectFt, ['traditional']),
    len('sillHornFt', 'Sill Horns', EXTERIOR_TRIM_DEFAULTS.sillHornFt, ['traditional']),
    len('apronFt', 'Apron Height', EXTERIOR_TRIM_DEFAULTS.apronFt, ['traditional']),
    len('stoneSillThickFt', 'Sill Thickness', EXTERIOR_TRIM_DEFAULTS.stoneSillThickFt, ['stoneSill']),
    len('stoneSillProjectFt', 'Sill Projection', EXTERIOR_TRIM_DEFAULTS.stoneSillProjectFt, ['stoneSill']),
    len('stoneSillHornFt', 'Sill Horns', EXTERIOR_TRIM_DEFAULTS.stoneSillHornFt, ['stoneSill']),
    len('brickSillHeightFt', 'Sill Height (front)', EXTERIOR_TRIM_DEFAULTS.brickSillHeightFt, ['brick']),
    len('brickSillProjectFt', 'Sill Projection', EXTERIOR_TRIM_DEFAULTS.brickSillProjectFt, ['brick']),
    len('brickSillHornFt', 'Sill Past Opening', EXTERIOR_TRIM_DEFAULTS.brickSillHornFt, ['brick']),
    {
      key: 'brickSillSlopeDeg', label: 'Sill Tilt (°)', kind: 'number' as const, default: EXTERIOR_TRIM_DEFAULTS.brickSillSlopeDeg, min: 0, max: 30,
      conditionalOn: { param: 'exteriorTrimStyle', equals: ['brick'] },
    },
    {
      key: 'lintelCourse', label: 'Lintel', kind: 'choice' as const, default: 'soldier',
      choices: (Object.keys(LINTEL_COURSES) as LintelCourse[]).map((k) => ({ value: k, label: LINTEL_COURSES[k].label })),
      conditionalOn: { param: 'exteriorTrimStyle', equals: ['brick'] },
    },
    len('lintelEndsFt', 'Lintel Past Opening', EXTERIOR_TRIM_DEFAULTS.lintelEndsFt, ['brick']),
    len('lintelProjectFt', 'Lintel Projection', EXTERIOR_TRIM_DEFAULTS.lintelProjectFt, ['brick']),
    len('tubeThickFt', 'Board Thickness', EXTERIOR_TRIM_DEFAULTS.tubeThickFt, ['modern']),
    len('tubeProjectFt', 'Projection', EXTERIOR_TRIM_DEFAULTS.tubeProjectFt, ['modern']),
    {
      // brick: blank = the wall's own exterior finish (the brick it's laid in)
      key: 'trimMaterial', label: 'Trim / Lintel Material', kind: 'material' as const, default: '',
      conditionalOn: { param: 'exteriorTrimStyle', equals: ['traditional', 'pictureFrame', 'modern', 'brick'] },
    },
    {
      key: 'sillMaterial', label: 'Sill Material', kind: 'material' as const, default: '',
      conditionalOn: { param: 'exteriorTrimStyle', equals: ['traditional', 'stoneSill', 'brick'] },
    },
  ],
}

/** the INSTANCE override — one opening can switch trim off or pick another style */
export const EXTERIOR_TRIM_INSTANCE_PARAM = {
  key: 'exteriorTrim', label: 'Exterior Trim', kind: 'choice' as const, default: '',
  choices: [
    { value: '', label: '(Use Type Default)' },
    { value: 'none', label: 'Off — finish returns to the frame' },
    ...EXTERIOR_TRIM_STYLES.filter((v) => v !== 'none').map((v) => ({ value: v, label: EXTERIOR_TRIM_LABELS[v] })),
  ],
}

/** BRICK on one opening: its lintel course ('' = the type's) — shown when the opening itself
 *  picks Brick (a type whose own style isn't brick holds no active lintel choice) */
export const BRICK_LINTEL_INSTANCE_PARAM = {
  key: 'lintel', label: 'Lintel', kind: 'choice' as const, default: '',
  choices: [
    { value: '', label: '(Use Type Default)' },
    ...(Object.keys(LINTEL_COURSES) as LintelCourse[]).map((k) => ({ value: k, label: LINTEL_COURSES[k].label })),
  ],
  conditionalOn: { param: 'exteriorTrim', equals: 'brick' },
}

/**
 * INTERIOR TRIM (2026-10-04, Matt: "add interior trims as instance parameters, too, using
 * similar styles but no need for brick sills and lintels on the interior"). Per opening
 * (`interiorTrim`, default none), built on the room-side face(s) by the host wall — both faces
 * of an interior wall. Same styles as the exterior minus the masonry ones; traditional's sill
 * becomes the STOOL (the interior sill) over an apron.
 */
export const INTERIOR_TRIM_STYLES = ['none', 'traditional', 'pictureFrame', 'stoneSill', 'modern'] as const
export type InteriorTrimStyle = (typeof INTERIOR_TRIM_STYLES)[number]
export const INTERIOR_TRIM_LABELS: Record<InteriorTrimStyle, string> = {
  none: 'None',
  traditional: 'Traditional — casings, head, stool & apron',
  pictureFrame: 'Picture frame — casing all around',
  // Matt, 2026-10-04: "a thick sill but kerf jamb and header where the drywall just turns
  // into the window"
  stoneSill: 'Thick sill — drywall returns at the jambs & head (kerf jamb)',
  modern: 'Modern — boards lining the opening, no casing',
}
/** interior casing sizes, in the exterior trim's own keys so one builder serves both */
export const INTERIOR_TRIM_SIZES: typeof EXTERIOR_TRIM_DEFAULTS = {
  ...EXTERIOR_TRIM_DEFAULTS,
  trimWidthFt: 3.5 * IN,
  trimHeadFt: 4.5 * IN,
  trimThickFt: 0.75 * IN,
  sillThickFt: 1 * IN,          // the stool
  sillProjectFt: 1 * IN,        // past the casing face
  sillHornFt: 0.75 * IN,        // past the casing, each end
  apronFt: 3.5 * IN,
  tubeThickFt: 0.75 * IN,
  tubeProjectFt: 0.5 * IN,
  jambExtThickFt: 0.75 * IN,
  stoneSillThickFt: 1.25 * IN,   // the thick interior sill
  stoneSillProjectFt: 1 * IN,    // past the drywall
  stoneSillHornFt: 1 * IN,       // past the opening, each end
}

/** total thickness of the INTERIOR finish layers (from the last layer back while 'finish') */
export function interiorFinishThicknessFt(assembly: AssemblyRecord | null | undefined): number {
  const layers = assembly?.layers ?? []
  let t = 0
  for (let i = layers.length - 1; i >= 0; i--) {
    if (layers[i].function !== 'finish') break
    t += layers[i].thicknessFt || 0
  }
  return t
}
export function resolveInteriorTrimStyle(p: Record<string, ParamValue | undefined>): InteriorTrimStyle {
  const v = p.interiorTrim
  return typeof v === 'string' && (INTERIOR_TRIM_STYLES as readonly string[]).includes(v) ? (v as InteriorTrimStyle) : 'none'
}
/** the INSTANCE params — door + window schemas */
export const INTERIOR_TRIM_INSTANCE_PARAMS = [
  {
    key: 'interiorTrim', label: 'Interior Trim', kind: 'choice' as const, default: 'none',
    choices: INTERIOR_TRIM_STYLES.map((v) => ({ value: v, label: INTERIOR_TRIM_LABELS[v] })),
  },
  {
    // blank = the default material
    key: 'interiorTrimMaterial', label: 'Interior Trim / Sill Material', kind: 'material' as const, default: '',
    conditionalOn: { param: 'interiorTrim', equals: ['traditional', 'pictureFrame', 'stoneSill', 'modern'] },
  },
]
